import React from 'react';
import PropTypes from 'prop-types';

const highlightInline = (text) => {
  if (!text) return text;
  
  const elements = [];
  let remaining = text;
  let keyIndex = 0;
  
  while (remaining.length > 0) {
    // Jinja blocks {% ... %}
    const jinjaBlockMatch = remaining.match(/^(.*?)\{%\s*(.*?)\s*%\}/);
    if (jinjaBlockMatch && jinjaBlockMatch.index === 0) {
      if (jinjaBlockMatch[1]) {
        elements.push(<span key={keyIndex++}>{jinjaBlockMatch[1]}</span>);
      }
      elements.push(
        <span 
          key={keyIndex++} 
          className="py-0.5 px-[5px] rounded-sm font-medium text-pink-400 bg-pink-400/15"
        >
          {`{% ${jinjaBlockMatch[2]} %}`}
        </span>
      );
      remaining = remaining.slice(jinjaBlockMatch[0].length);
      continue;
    }
    
    // Jinja variables {{ ... }}
    const jinjaVarMatch = remaining.match(/^(.*?)\{\{\s*(.*?)\s*\}\}/);
    if (jinjaVarMatch && jinjaVarMatch.index === 0) {
      if (jinjaVarMatch[1]) {
        elements.push(<span key={keyIndex++}>{jinjaVarMatch[1]}</span>);
      }
      elements.push(
        <span 
          key={keyIndex++} 
          className="py-0.5 px-[5px] rounded-sm font-medium text-green-400 bg-green-400/15"
        >
          {`{{ ${jinjaVarMatch[2]} }}`}
        </span>
      );
      remaining = remaining.slice(jinjaVarMatch[0].length);
      continue;
    }
    
    // Inline code `...`
    const codeMatch = remaining.match(/^(.*?)`([^`]+)`/);
    if (codeMatch && codeMatch.index === 0) {
      if (codeMatch[1]) {
        elements.push(<span key={keyIndex++}>{codeMatch[1]}</span>);
      }
      elements.push(
        <span 
          key={keyIndex++} 
          className="py-[2px] px-[5px] rounded font-mono text-white bg-neutral-800 border border-transparent"
        >
          {codeMatch[2]}
        </span>
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }
    
    // Bold **...**
    const boldMatch = remaining.match(/^(.*?)\*\*([^*]+)\*\*/);
    if (boldMatch && boldMatch.index === 0) {
      if (boldMatch[1]) {
        elements.push(<span key={keyIndex++}>{boldMatch[1]}</span>);
      }
      elements.push(
        <span 
          key={keyIndex++} 
          className="font-semibold text-white"
        >
          {boldMatch[2]}
        </span>
      );
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }
    
    elements.push(
      <span key={keyIndex++} className="text-white">{remaining}</span>
    );
    break;
  }
  
  return elements;
};

const highlightMarkdown = (text) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  
  return lines.map((line, lineIndex) => {
    // Headers
    const headerMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headerMatch) {
      return (
        <span key={lineIndex}>
          <span className="text-cyan-300 font-normal">
            {headerMatch[1]}{' '}
          </span>
          <span className="font-semibold text-white">
            {highlightInline(headerMatch[2])}
          </span>
          {'\n'}
        </span>
      );
    }
    
    // Bullet lists
    const bulletMatch = line.match(/^(\s*)([-*])\s+(.*)$/);
    if (bulletMatch) {
      return (
        <span key={lineIndex}>
          {bulletMatch[1]}
          <span className="text-cyan-300 font-semibold">
            {bulletMatch[2]}
          </span>{' '}
          {highlightInline(bulletMatch[3])}
          {'\n'}
        </span>
      );
    }
    
    // Numbered lists
    const numMatch = line.match(/^(\s*)(\d+\.)\s+(.*)$/);
    if (numMatch) {
      return (
        <span key={lineIndex}>
          {numMatch[1]}
          <span className="text-cyan-300 font-semibold">
            {numMatch[2]}
          </span>{' '}
          {highlightInline(numMatch[3])}
          {'\n'}
        </span>
      );
    }
    
    // Regular text
    return (
      <span key={lineIndex} className="text-white">
        {highlightInline(line)}
        {'\n'}
      </span>
    );
  });
};

const MarkdownHighlighter = ({ text, className }) => {
  return (
    <pre 
      className={`m-0 font-mono text-[13px] leading-[1.8] whitespace-pre-wrap break-words text-white ${className || ''}`}
    >
      {highlightMarkdown(text)}
    </pre>
  );
};

MarkdownHighlighter.propTypes = {
  text: PropTypes.string,
  className: PropTypes.string,
};

MarkdownHighlighter.defaultProps = {
  text: '',
  className: '',
};

export default MarkdownHighlighter;
export { highlightMarkdown };
